import { Link } from "react-router-dom";
import { ArrowLeft, Compass, MoreVertical, PlusSquare, Share, Smartphone, WifiOff } from "lucide-react";
import LegalFooter from "@/components/LegalFooter";

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

const Step = ({ n, children }) => (
  <li className="flex gap-3">
    <span className="w-6 h-6 shrink-0 border border-[#1f1f1f] bg-[#f7f5f0] flex items-center justify-center font-mono text-[11px]">
      {n}
    </span>
    <span className="text-[15px] leading-relaxed text-[#3d3d3d]">{children}</span>
  </li>
);

const PlatformCard = ({ id, badge, title, subtitle, children, highlight }) => (
  <section
    data-testid={`install-${id}`}
    className={`tactile-card p-6 sm:p-8 ${highlight ? "ring-2 ring-[#b85c47] ring-offset-2 ring-offset-[#f7f5f0]" : ""}`}
  >
    <div className="flex items-baseline justify-between gap-3">
      <h2 className="font-serif text-2xl sm:text-3xl">{title}</h2>
      {highlight && (
        <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#b85c47] border border-[#b85c47] px-2 py-0.5 whitespace-nowrap">
          Your device
        </span>
      )}
    </div>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8a8272] mt-1">{badge} · {subtitle}</p>
    <ol className="mt-5 space-y-3.5">{children}</ol>
  </section>
);

export default function Install() {
  const ios = isIOS();
  const iphone = (
    <PlatformCard key="ios" id="iphone" badge="iPhone / iPad" title="On iPhone" subtitle="Safari" highlight={ios}>
      <Step n={1}>
        Open <span className="font-medium">pinmy.travel</span> in <span className="font-medium">Safari</span> and log in.
      </Step>
      <Step n={2}>
        Tap the <span className="font-medium">Share</span> button <Share className="inline w-4 h-4 -mt-0.5 text-[#2c4a3e]" strokeWidth={1.75} /> at the bottom of the screen.
      </Step>
      <Step n={3}>
        Scroll down and tap <span className="font-medium">Add to Home Screen</span> <PlusSquare className="inline w-4 h-4 -mt-0.5 text-[#2c4a3e]" strokeWidth={1.75} />, then <span className="font-medium">Add</span>.
      </Step>
      <Step n={4}>Pins now lives on your home screen like any other app.</Step>
    </PlatformCard>
  );
  const android = (
    <PlatformCard key="android" id="android" badge="Android" title="On Android" subtitle="Chrome" highlight={!ios && /Android/.test(navigator.userAgent)}>
      <Step n={1}>
        Open <span className="font-medium">pinmy.travel</span> in <span className="font-medium">Chrome</span> and log in.
      </Step>
      <Step n={2}>
        Tap the <span className="font-medium">menu</span> <MoreVertical className="inline w-4 h-4 -mt-0.5 text-[#2c4a3e]" strokeWidth={1.75} /> in the top-right corner.
      </Step>
      <Step n={3}>
        Tap <span className="font-medium">Add to Home screen</span> (on some phones: <span className="font-medium">Install app</span>), then confirm.
      </Step>
      <Step n={4}>Pins installs like a normal app — icon, full screen, the lot.</Step>
    </PlatformCard>
  );

  return (
    <div className="min-h-screen bg-[#f7f5f0] text-[#1f1f1f]">
      <div className="max-w-2xl mx-auto px-6 py-16" data-testid="install-page">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#5c5c5c] hover:text-[#1f1f1f]"
          data-testid="install-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.75} /> Back to Pins
        </Link>
        <p className="overline text-[#b85c47] mt-8 flex items-center gap-1.5">
          <Smartphone className="w-3 h-3" strokeWidth={1.5} /> pinmy.travel
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl mt-2">Get Pins on your phone</h1>
        <p className="mt-4 text-[15px] leading-relaxed text-[#3d3d3d] max-w-lg">
          No app store needed — Pins installs straight from your browser in about ten seconds.
          Same journal, full screen, with an icon on your home screen.
        </p>
        <div className="mt-8 space-y-6">
          {ios ? [iphone, android] : [android, iphone]}
        </div>
        <div className="mt-6 flex items-start gap-3 border border-[#d5cfc1] bg-[#efebe1] p-4" data-testid="install-offline-note">
          <WifiOff className="w-4 h-4 mt-0.5 shrink-0 text-[#2c4a3e]" strokeWidth={1.75} />
          <p className="text-sm text-[#3d3d3d]">
            Once installed, Pins keeps working off the grid — draft pins and browse cached maps without signal,
            and everything syncs when you're back online.
          </p>
        </div>
        <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.18em] text-[#8a8272] flex items-center gap-1.5">
          <Compass className="w-3 h-3" strokeWidth={1.5} /> Official app-store listings are on the way
        </p>
        <LegalFooter />
      </div>
    </div>
  );
}
